import React from 'react';
import { Box, Button, Checkbox, Chip, TableCell, TableRow, Typography } from '@mui/material';
import { VisibilityOutlined as VisibilityOutlinedIcon } from '@mui/icons-material';
import { ManagerCommentCell } from '~/components/CommentCell/ManagerCommentCell.tsx';
import { DocumentGroupRow } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ReviewDocuments/DocumentGroupRow.tsx';
import { MANAGER_APPLICATION_REVIEW_TABLE_LABELS } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import {
  formatManagerSignerLabel,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/reviewUtils.ts';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { managerStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/styles.ts';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import { StatusFilterValue } from '~/modules/covenants/types.ts';
import type {
  TManagerApplicationDecision,
  TManagerReviewFile,
  TManagerReviewTableRow,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import { StatusChipWithTooltip } from '~/modules/covenants/components/StatusChipWithTooltip.tsx';
import { getManagerApplicationDocumentStatusConfig } from '~/modules/covenants/statusConfig.ts';
import { tableRowStyles } from '~/modules/covenants/utils.ts';

interface IManagerApplicationDocumentTableRowProps {
  row: TManagerReviewTableRow;
  decisions: Record<string, TManagerApplicationDecision>;
  isDecisionUpdating: boolean;
  isReadOnly: boolean;
  isViewed: boolean;
  getDocumentCommentValue: (documentId: string) => string;
  onPreviewDocument: (document: TManagerReviewFile) => void;
  onDecisionChange: (
    documentId: string,
    decision: Exclude<TManagerApplicationDecision, null>
  ) => void;
  onCommentSave: (documentId: string, comment: string) => Promise<void> | void;
}

export const DocumentRow: React.FC<
  IManagerApplicationDocumentTableRowProps
> = ({
  row,
  decisions,
  isDecisionUpdating,
  isReadOnly,
  isViewed,
  getDocumentCommentValue,
  onPreviewDocument,
  onDecisionChange,
  onCommentSave,
}) => {
  if (row.type === 'document') {
    return <DocumentGroupRow document={row.document} hasFiles={row.hasFiles} />;
  }

  const { file } = row;
  const documentDecision = decisions[file.id] ?? null;
  const commentValue = getDocumentCommentValue(file.id);
  const isAcceptSelected = documentDecision === 'accept';
  const documentStatusConfig = getManagerApplicationDocumentStatusConfig(file.status);
  const viewedStatusLabel = isViewed
    ? MANAGER_APPLICATION_REVIEW_TABLE_LABELS.viewed
    : MANAGER_APPLICATION_REVIEW_TABLE_LABELS.notViewed;

  return (
    <TableRow
      hover
      sx={(theme) => ({
        ...tableRowStyles(managerStyles.tableRow, theme),
        ...tableRowStyles(managerComponentStyles.docsFileRow, theme),
      })}
    >
      <TableCell sx={managerComponentStyles.docsBodyCell}>
        <Box sx={managerComponentStyles.docsFileNameWrap}>
          <Typography variant="body2" sx={managerComponentStyles.docsFileName}>
            {file.fileName || ''}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="center" sx={managerComponentStyles.docsBodyCell} />
      <TableCell align="center" sx={managerComponentStyles.docsBodyCell}>
        <StatusChipWithTooltip
          label={documentStatusConfig.label}
          color={documentStatusConfig.color}
          description={documentStatusConfig.description}
          chipSx={(theme) => ({
            ...tableRowStyles(commonStyles.statusChip, theme),
            ...(file.status === StatusFilterValue.Sent
              ? tableRowStyles(managerComponentStyles.sentChip, theme)
              : {}),
          })}
        />
      </TableCell>
      <TableCell align="center" sx={managerComponentStyles.docsBodyCell}>
        <Box sx={managerComponentStyles.docsComment}>
          {!isAcceptSelected && (
            <ManagerCommentCell
              initialComment={commentValue}
              readOnly={isReadOnly}
              onSave={async (newComment) => {
                await onCommentSave(file.id, newComment);
              }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell align="center" sx={managerComponentStyles.docsBodyCell}>
        <Typography variant="body2" sx={managerComponentStyles.docsSignerText}>
          {file.signedByName || file.signetByCode ? formatManagerSignerLabel(file) : ''}
        </Typography>
      </TableCell>
      <TableCell align="center" sx={managerComponentStyles.docsBodyCell} />
      <TableCell align="center" sx={managerComponentStyles.docsBodyCell}>
        <Box sx={managerComponentStyles.docsPreviewStack}>
          <Button
            variant="contained"
            size="small"
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
            onClick={() => onPreviewDocument(file)}
            sx={managerComponentStyles.docsPreviewBtn}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {MANAGER_APPLICATION_REVIEW_TABLE_LABELS.previewButton}
            </Box>
          </Button>
          <Chip
            size="small"
            variant="filled"
            label={viewedStatusLabel}
            sx={managerComponentStyles.docsViewedChip(isViewed)}
          />
        </Box>
      </TableCell>
      <TableCell align="center" sx={managerComponentStyles.docsBodyCell}>
        <Checkbox
          checked={documentDecision === 'accept'}
          onChange={() => {
            void onDecisionChange(file.id, 'accept');
          }}
          disabled={isReadOnly || isDecisionUpdating}
          sx={managerComponentStyles.decisionCheckbox(isReadOnly || isDecisionUpdating)}
          slotProps={{ input: { 'aria-label': `accept-${file.id}` } }}
        />
      </TableCell>
      <TableCell align="center" sx={managerComponentStyles.docsBodyCell}>
        <Checkbox
          checked={documentDecision === 'reject'}
          onChange={() => {
            void onDecisionChange(file.id, 'reject');
          }}
          disabled={isReadOnly || isDecisionUpdating}
          sx={managerComponentStyles.decisionCheckbox(isReadOnly || isDecisionUpdating)}
          slotProps={{ input: { 'aria-label': `reject-${file.id}` } }}
        />
      </TableCell>
    </TableRow>
  );
};

